import React, { useEffect, useState } from "react";
import { propertyStats } from "../actions/propertyStats";
import { userBalance } from "../actions/userBalance";
import { useParams, useNavigate } from "react-router-dom";
import { propertyByUser } from "../actions/propertyByUser";
import { userByIdPreview } from "../actions/userById";
import { cloudinaryUrl } from "../constants/CloudinaryConstants";

const UserProperties = () => {
  const { id } = useParams();
  const [properties, setProperties] = useState([]);
  const [propStats, setPropStats] = useState({});
  const [keyBalance, setKeyBalance] = useState(0);
  const [poster, setPoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (id) {
      userByIdPreview(id, { setPoster });
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      await propertyByUser(
        {
          setProperties: (data) =>
            active && setProperties(Array.isArray(data) ? data : []),
        },
        id
      );
      propertyStats({ setPropStats });
      userBalance(id, { setBalance: setKeyBalance });
      if (active) setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [id]);

  const activeProperties = (properties || []).filter(
    (p) => p.property_status === "Active"
  );

  return (
    <div className="min-h-screen bg-white py-8 sm:py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              {poster?.legal_first_name || "User"}'s Active Properties
            </h1>
            <div className="mt-2 flex gap-6 text-sm text-slate-700">
              <span>
                Keys Earned:{" "}
                <span className="font-bold text-blue-700">{keyBalance}</span>
              </span>
              <span>
                Impressions:{" "}
                <span className="font-bold text-green-700">
                  {propStats?.impressions || 0}
                </span>
              </span>
              <span>
                Sold:{" "}
                <span className="font-bold text-red-700">
                  {propStats?.sold || 0}
                </span>
              </span>
              <span>
                Leased:{" "}
                <span className="font-bold text-teal-700">
                  {propStats?.leased || 0}
                </span>
              </span>
            </div>
          </div>
          <button
            onClick={() => nav(-1)}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
          >
            ← Back
          </button>
        </div>

        {loading ? (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
            Loading properties...
          </div>
        ) : !activeProperties.length ? (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
            No active properties found for this user.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {activeProperties.map((property) => {
              const previewImage = property?.media_url
                ? cloudinaryUrl(property.media_url.split(",")[0])
                : null;
              const address =
                [property?.street_number, property?.street_name]
                  .filter(Boolean)
                  .join(" ") ||
                property?.title ||
                "Untitled property";
              const location = [property?.city, property?.state]
                .filter(Boolean)
                .join(", ");
              return (
                <div
                  key={property.property_id}
                  className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white"
                >
                  <div className="relative h-32 bg-slate-100">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt={address}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-slate-400 text-sm">
                        No photo yet
                      </div>
                    )}
                    <span className="absolute top-2 left-2 px-2 py-1 text-xs font-semibold rounded-full bg-white/90 text-slate-800 border border-slate-200">
                      {property.property_status}
                    </span>
                  </div>
                  <div className="p-3 space-y-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {address}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {location || "Location pending"}
                    </p>
                    <p className="text-sm font-bold text-blue-700">
                      {property?.price
                        ? `$${Number(property.price).toFixed(0)}`
                        : "Price not set"}
                      {property?.for === "Lease" ? "/mo" : ""}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span>{property?.type || property?.for || "Type"}</span>
                      <span>
                        {property?.beds_total || 0} bd ·{" "}
                        {property?.baths_full || 0} ba
                      </span>
                    </div>
                    <button
                      type="button"
                      className="w-full mt-2 bg-slate-900 text-white py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors"
                      onClick={() => {
                        const pid = property?.property_id;
                        if (!pid || !/^\d+$/.test(String(pid))) {
                          if (process.env.NODE_ENV !== "production") {
}
                          return;
                        }
                        nav(`/property/${pid}`);
                      }}
                    >
                      View details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProperties;
